import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import type { Schema } from '@tiptap/pm/model'
import { createSyncedRows } from '../services/sync/syncedRows'
import { useCorpusStore, type CorpusNode } from './corpus'
import { countDocWords } from '../utils/editorWordCount'
import { countWordsFromContent } from '../utils/contentUtils'

// The last known word count of each page, so totals across a desk or the
// corpus don't require loading every page. Counts sync as their own rows
// rather than in the tree's node metadata: tree rows are last-writer-wins as
// a whole, so a frequently changing count there could overwrite a rename or
// move made on another device.

const BACKFILL_CONCURRENCY = 4

function parseCount(raw: unknown): number | null {
  return typeof raw === 'number' && Number.isFinite(raw) && raw >= 0 ? Math.round(raw) : null
}

export interface ScopeTotal {
  words: number
  pageIds: string[]
  /** Pages with no known count yet; `words` leaves them out until counted. */
  missing: string[]
}

export const useWordCountStore = defineStore('wordCounts', () => {
  const corpus = useCorpusStore()
  const rows = createSyncedRows<number>({
    storageKey: 'ink-word-counts',
    remotePrefix: 'wordcount.',
    parse: parseCount,
    pushDelayMs: 10_000,
  })
  const counts = rows.values

  /** Records a page's current count; unchanged counts write nothing. */
  function record(pageId: string, words: number) {
    if (counts.value[pageId] !== words) rows.write(pageId, words)
  }

  /** Pages (notes included) under a desk or folder, or in the whole corpus when null. */
  function pagesInScope(scopeId: string | null): CorpusNode[] {
    const roots = scopeId === null ? corpus.tree : corpus.findNode(scopeId)?.children ?? []
    const pages: CorpusNode[] = []
    const walk = (nodes: CorpusNode[]) => {
      for (const node of nodes) {
        if (node.subtype === 'page' && node.includeInWordCount !== false) pages.push(node)
        if (node.children?.length) walk(node.children)
      }
    }
    walk(roots)
    return pages
  }

  function scopeTotal(scopeId: string | null): ScopeTotal {
    const pageIds = pagesInScope(scopeId).map((page) => page.id)
    let words = 0
    const missing: string[] = []
    for (const id of pageIds) {
      const count = counts.value[id]
      if (count === undefined) missing.push(id)
      else words += count
    }
    return { words, pageIds, missing }
  }

  // ── Counting pages that have never been opened ────────────────────────────

  const schema = shallowRef<Schema | null>(null)
  /** The editor's schema, so pages read from disk count exactly as the editor would. */
  function setSchema(value: Schema | null) {
    schema.value = value
  }

  const counting = ref(false)
  const attempted = new Set<string>()

  async function countPage(pageId: string): Promise<number | null> {
    const api = (window as any).api
    if (!api?.readPage) return null
    const res = await api.readPage(pageId)
    if (!res?.success || res.content === undefined) return null
    if (!schema.value) return countWordsFromContent(res.content)
    const json = typeof res.content === 'string' ? JSON.parse(res.content) : res.content
    return countDocWords(schema.value.nodeFromJSON(json))
  }

  /** Counts pages with no known count, a few at a time, each at most once per session. */
  async function backfill(pageIds: string[]) {
    const todo = pageIds.filter((id) => counts.value[id] === undefined && !attempted.has(id))
    if (todo.length === 0 || counting.value) return
    counting.value = true
    todo.forEach((id) => attempted.add(id))
    const next = async (): Promise<void> => {
      const id = todo.shift()
      if (!id) return
      try {
        const words = await countPage(id)
        if (words !== null && counts.value[id] === undefined) record(id, words)
      } catch (err) {
        console.warn('[WordCounts] Could not count page', id, err)
      }
      return next()
    }
    try {
      // Pages may come from the cloud on a new device; a few at once keeps it quick.
      await Promise.all(Array.from({ length: BACKFILL_CONCURRENCY }, next))
    } finally {
      counting.value = false
    }
  }

  return {
    counts,
    counting,
    record,
    pagesInScope,
    scopeTotal,
    setSchema,
    backfill,
    syncWithRemote: rows.syncWithRemote,
  }
})
